import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiClock, FiUsers, FiHash, FiStar, FiCopy, FiSmartphone } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';
import { contatosAPI } from '../../services/api';
import toast from 'react-hot-toast';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  color: ${theme.colors.text.primary};
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  text-transform: uppercase;
  border: 3px solid rgba(255, 255, 255, 0.3);
  color: ${theme.colors.text.primary};
`;

const InfoContainer = styled.div`
  flex: 1 1 auto;
  margin-left: ${theme.spacing[4]};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const Name = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0;
  word-break: break-word;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.fontSizes.sm};
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const DetailActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.xs};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 2px ${theme.colors.primary[500]}20;
  }
`;

const HeaderCopyButton = styled(CopyButton)`
  border-color: rgba(255, 255, 255, 0.5);
  color: ${theme.colors.text.primary};

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: ${theme.colors.text.primary};
    border-color: rgba(255, 255, 255, 0.8);
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.8);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
  }
`;

const ClientBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.primary[500]}20;
  color: ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
`;

const TypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${props => props.tipo === 'C' ? `${theme.colors.success[500]}20` : `${theme.colors.primary[500]}20`};
  color: ${props => props.tipo === 'C' ? theme.colors.success[600] : theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
`;

const StarButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props =>
    props.$favorito ? theme.colors.warning[400] : 'rgba(255, 255, 255, 0.85)'};
  transition: all 0.2s ease;
  font-size: ${theme.fontSizes.xl};

  &:hover {
    color: ${props =>
      props.$favorito ? theme.colors.warning[500] : 'rgba(255, 255, 255, 1)'};
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const BadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getInitials = (name) => {
  if (!name) return 'C';
  const words = name.trim().split(' ');
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const extractPhoneDigits = (sessionKey) => {
  if (!sessionKey) return '';
  const [rawNumber] = sessionKey.split('@');
  if (!rawNumber) return '';
  return rawNumber.replace(/\D/g, '');
};

const formatPhoneNumber = (sessionKey) => {
  const digits = extractPhoneDigits(sessionKey);
  if (!digits) return sessionKey || 'N/A';

  let number = digits;
  if (number.startsWith('55') && number.length > 11) {
    number = number.slice(2);
  }

  if (number.length === 11) {
    const ddd = number.slice(0, 2);
    const prefix = number.slice(2, 7);
    const suffix = number.slice(7);
    return `(${ddd}) ${prefix}-${suffix}`;
  }

  if (number.length === 10) {
    const ddd = number.slice(0, 2);
    const prefix = number.slice(2, 6);
    const suffix = number.slice(6);
    return `(${ddd}) ${prefix}-${suffix}`;
  }

  return number;
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Número copiado!');
  } catch (error) {
    console.error('Erro ao copiar número:', error);
    toast.error('Não foi possível copiar o número');
  }
};

const ContatoCard = ({ contato, onFavoritoChange }) => {
  const navigate = useNavigate();
  // Inicializar com o valor real do contato (true se for true, false caso contrário, default true se null/undefined)
  const [favorito, setFavorito] = useState(contato.favorito === true || contato.favorito === null || contato.favorito === undefined);
  const [atualizandoFavorito, setAtualizandoFavorito] = useState(false);

  // Sincronizar estado quando o contato mudar
  useEffect(() => {
    // Se favorito for explicitamente false, usar false; caso contrário, usar true (default)
    setFavorito(contato.favorito === true || contato.favorito === null || contato.favorito === undefined);
  }, [contato.favorito, contato.session_key]);

  const handleCardClick = () => {
    navigate(`/whatsapp/conversas?chatId=${contato.session_key}`);
  };

  const handleFavoritarClick = async (e) => {
    e.stopPropagation(); // Prevenir navegação ao clicar na estrela
    
    const novoFavorito = !favorito;
    setAtualizandoFavorito(true);

    try {
      // Enviar id_cliente junto com a requisição para garantir unicidade
      const response = await contatosAPI.favoritar(
        contato.session_key, 
        novoFavorito, 
        contato.id_cliente || null
      );
      
      // Atualizar estado com o valor retornado do servidor
      if (response.data && response.data.data) {
        setFavorito(response.data.data.favorito);
      } else {
        setFavorito(novoFavorito);
      }
      
      // Callback para atualizar lista se necessário
      if (onFavoritoChange) {
        onFavoritoChange(contato.session_key, novoFavorito);
      }
    } catch (error) {
      console.error('Erro ao favoritar/desfavoritar:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar favorito';
      toast.error(errorMessage);
      // Reverter estado em caso de erro
      setFavorito(!novoFavorito);
    } finally {
      setAtualizandoFavorito(false);
    }
  };

  const getTipoLabel = () => {
    if (contato.tipo === 'C') return 'Celular';
    if (contato.tipo === 'W') return 'WhatsApp';
    return 'Desconhecido';
  };

  return (
    <CardContainer onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <Header>
        <Avatar>
          {getInitials(contato.nome || contato.session_key)}
        </Avatar>
        <InfoContainer>
          <Name>{contato.nome || 'Sem nome'}</Name>
          <Subtitle>
            <span>{formatPhoneNumber(contato.session_key)}</span>
            <HeaderCopyButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(formatPhoneNumber(contato.session_key));
              }}
              title="Copiar número formatado"
              aria-label="Copiar número formatado"
            >
              <FiCopy />
              Copiar
            </HeaderCopyButton>
          </Subtitle>
        </InfoContainer>
        <HeaderActions>
          <StarButton
            $favorito={favorito}
            onClick={handleFavoritarClick}
            disabled={atualizandoFavorito}
            title={favorito ? 'Desfavoritar' : 'Favoritar'}
          >
            <FiStar fill={favorito ? 'currentColor' : 'none'} />
          </StarButton>
        </HeaderActions>
      </Header>

      <ContentSection>
        <Details>
          <DetailItem>
            <DetailIcon><FiHash /></DetailIcon>
            <span>{contato.session_key}</span>
          </DetailItem>

          {contato.cliente_nome && (
            <DetailItem>
              <DetailIcon><FiUsers /></DetailIcon>
              <span>{contato.cliente_nome}</span>
            </DetailItem>
          )}

          <DetailItem>
            <DetailIcon><FiClock /></DetailIcon>
            <span>Última atualização: {formatDate(contato.updated_at)}</span>
          </DetailItem>

          <DetailItem>
            <BadgesContainer>
              {contato.tipo && (
                <TypeBadge tipo={contato.tipo}>
                  {getTipoLabel()}
                </TypeBadge>
              )}
            </BadgesContainer>
          </DetailItem>
        </Details>
      </ContentSection>
    </CardContainer>
  );
};

export default ContatoCard;

